import React, { PropsWithChildren } from 'react';
import useTheme from '../../hooks/ui/useTheme';
import { ButtonSizes, ButtonTypes } from '../../services/exports/Interfaces';
import LoadingIndicator from '../loading/LoadingIndicator';
import classnames from 'classnames';
import CircleArrow from '../../assets/icons/CircleArrow';
import useScreenType from '../../hooks/utility/useScreenType';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';

export interface CustomButtonProps extends PropsWithChildren {
  testId?: string;
  title?: string | React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  titleClass?: string;
  buttonType?: ButtonTypes;
  buttonSize?: ButtonSizes;
  containerStyle?: string;
  fontStyle?: string;
  customGradient?: string;
  disabled?: boolean;
  loading?: boolean;
  customIcon?: any;
  showArrow?: boolean;
  onClick?: () => void;
}

const CustomButton = (props: CustomButtonProps) => {
  const {
    testId,
    title,
    type,
    className,
    titleClass,
    buttonType,
    buttonSize,
    customGradient,
    disabled,
    loading,
    customIcon,
    containerStyle,
    fontStyle,
    showArrow,
    onClick,
    children,
  } = props;
  const theme = useTheme();
  const { isMobileApp } = useMobileApp();

  function getLoadingColor() {
    switch (buttonType) {
      case ButtonTypes.third:
        return theme.buttons[ButtonTypes.third].text;
      default:
        return theme.buttons[ButtonTypes.primary].text;
    }
  }

  function renderLoadingIndicator() {
    return loading ? (
      <div className={loadingIndicatorContainer} data-test={`${testId}-loading-indicator`}>
        <LoadingIndicator customColor={getLoadingColor()} lg={20} md={20} sm={20} />
      </div>
    ) : undefined;
  }

  function getBackgroundColor() {
    if (disabled) {
      return 'bg-brand-button-disabled-background dark:cursor-not-allowed';
    }

    switch (buttonType) {
      case ButtonTypes.secondary:
        return 'bg-brand-button-secondary-background';
      case ButtonTypes.third:
        return 'bg-brand-button-third-background border border-brand-button-third-border';
      case ButtonTypes.forth:
        return 'bg-brand-button-forth-background border border-brand-button-forth-border';
      case ButtonTypes.fifth:
        return 'border rounded-lg px-md py-xs';
      case ButtonTypes.sixth:
        return 'bg-brand-button-sixth-background p-xs rounded-lg';
      case ButtonTypes.disabled:
        return 'border border-brand-button-disabled-border';
      case ButtonTypes.danger:
        return 'bg-brand-button-danger-background';
      case ButtonTypes.warning:
        return 'bg-brand-button-warning-background';
      default:
        if (customGradient) {
          return customGradient;
        }

        return 'bg-brand-button-primary-background';
    }
  }

  function getTitleColor() {
    if (disabled) {
      return 'text-brand-button-disabled-text';
    }

    switch (buttonType) {
      case ButtonTypes.secondary:
        return 'text-brand-button-secondary-text';
      case ButtonTypes.third:
        return 'text-brand-button-third-text';
      case ButtonTypes.forth:
        return 'text-brand-button-forth-text';
      case ButtonTypes.fifth:
        return 'text-brand-button-fifth-text text-sm font-medium';
      case ButtonTypes.sixth:
        return 'text-brand-button-sixth-text text-sm';
      case ButtonTypes.disabled:
        return 'text-brand-button-disabled-text';
      case ButtonTypes.warning:
        return 'text-brand-button-warning-text';
      default:
        return 'text-brand-button-primary-text';
    }
  }

  const getTextColor = () => {
    if (disabled) {
      return theme.buttons[ButtonTypes.disabled].text;
    }

    switch (buttonType) {
      case ButtonTypes.secondary:
        return theme.buttons[ButtonTypes.secondary].text;
      case ButtonTypes.third:
        return theme.buttons[ButtonTypes.third].text;
      case ButtonTypes.forth:
        return theme.buttons[ButtonTypes.forth].text;
      case ButtonTypes.fifth:
        return theme.buttons[ButtonTypes.fifth].text;
      case ButtonTypes.sixth:
        return theme.buttons[ButtonTypes.sixth].text;
      case ButtonTypes.disabled:
        return theme.buttons[ButtonTypes.disabled].text;
      case ButtonTypes.warning:
        return theme.buttons[ButtonTypes.warning].text;
      default:
        return theme.buttons[ButtonTypes.primary].text;
    }
  };

  function getContainer() {
    switch (buttonSize) {
      case ButtonSizes.small:
        return tinyContainer;
      default:
        return container;
    }
  }

  const _onClick = () => {
    if (disabled || loading || !onClick) {
      return;
    }

    return onClick();
  };

  return (
    <button
      data-test={testId}
      type={type ?? 'button'}
      disabled={disabled}
      className={classnames(getContainer(), getBackgroundColor(), containerStyle ?? '', className, {
        'min-h-[43px] !py-[10px]': isMobileApp,
      })}
      onClick={_onClick}
    >
      {customIcon && !loading ? customIcon : null}
      {!children && (
        <>
          <p
            className={classnames(titleStyle, getTitleColor(), titleClass, fontStyle ?? '', {
              invisible: loading,
              '!text-[17px]': isMobileApp,
            })}
          >
            {title}
          </p>
          {showArrow && <CustomButton.Arrow loading={loading} disabled={disabled} color={getTextColor()} />}
        </>
      )}
      {children}

      {renderLoadingIndicator()}
    </button>
  );
};

export default CustomButton;

CustomButton.Arrow = ({ loading, disabled, color }: { loading?: boolean; disabled?: boolean; color?: string }) => {
  const { isDesktop } = useScreenType();

  if (loading || !isDesktop || disabled) return null;

  return (
    <CircleArrow
      className={classnames(
        'w-0 group-hover:w-6 ml-2',
        'h-6',
        '-rotate-45',
        'opacity-0 translate-x-[-10px]',
        'group-hover:opacity-100 group-hover:translate-x-0',
        'transition-all duration-200 ease-in-out',
        'overflow-hidden',
      )}
      color={color}
    />
  );
};

const container =
  'px-md py-sm rounded-lg shadow-md dark:shadow-none w-full flex flex-row items-center justify-center relative shadow transition-all duration-200 ease-in-out group';

const tinyContainer = 'p-sm rounded w-full relative';

const titleStyle = 'text-center visible w-full contents capitalize text-sm font-medium';

const loadingIndicatorContainer = 'absolute inset-0 flex justify-center items-center';
